import * as React from 'react'
import { ItemQuotationBase } from '../../../../services/json-schemas/base/item_quotation'
import { I18nService } from '../../../../services/utils/i18n.service'
import InputTextComponent from '../../../shared/input-text.component'
import { OfferedServiceBase } from '../../../../services/json-schemas/base/offered_service'
import { Select } from '../../../shared/select.component'
import { TypedID } from '../../../../services/utils/iots.service'
import { StyledInputDecimal } from '../../../shared/input-decimal.component'
import { QuotationBase } from '../../../../services/json-schemas/base/quotation'
import { Grid } from '@mui/material'
import { currencyWrapTaxNotice } from '../../../../services/utils/currency.service'
import QuotationDescriptionInputComponent from './quotation-description-input.component'

interface Props {
  index: number
  taxes: { id: TypedID<'TaxRulesGroup'>, text: string }[]
  quotation: Pick<QuotationBase, 'round_on_vat_price'>
  itemQuotation: Pick<ItemQuotationBase, 'description' | 'kind' | 'price_cents' | 'quantity' | 'tax_rules_group_id' | 'verbose_description'> & { id?: ItemQuotationBase['id'], _destroy?: boolean }
  // The kind of the item is only selectable if choices are provided
  kindChoices?: { value: ItemQuotationBase['kind'], label: string }[]
  offeredServiceDescription?: OfferedServiceBase['description_plain_text']
  disabledDescriptionBecauseSingleItem?: boolean
  layout?: (elements: Record<'description' | 'kind' | 'tax_rules_group_id' | 'quantity' | 'price_cents' | 'verbose_description', React.ReactNode>) => React.ReactNode
  onChange: (itemQuotation: Partial<Props['itemQuotation']>) => void
}

const i18nScoped = new I18nService(require('./quotation-form-item-quotation.component.yml'))

export default function QuotationFormItemQuotationComponent ({ index, quotation, itemQuotation, kindChoices, offeredServiceDescription, taxes, disabledDescriptionBecauseSingleItem, layout, onChange }: Props) {
  const unitPriceLabel = currencyWrapTaxNotice(i18nScoped.t('price'), quotation.round_on_vat_price)

  const parameters: Parameters<typeof layout>[0] = {
    description: <>
      <label>{ i18nScoped.t('description') }</label>

      { !disabledDescriptionBecauseSingleItem && <InputTextComponent
        id={ `item_quotation_${index}_description` }
        value={ itemQuotation.description }
        placeholder={ i18nScoped.t('description') }
        onChange={ description => onChange({ description }) } /> }

      { disabledDescriptionBecauseSingleItem && <input type="text" disabled value='' placeholder={ i18nScoped.t('description_disabled_single_item') } /> }
    </>,
    kind: kindChoices && <>
      <label>{ i18nScoped.t('kind') }</label>
      <Select
        id={ `item_quotation_${index}_kind` }
        placeholder={ i18nScoped.t('kind') }
        value={ kindChoices.find(_ => _.value === itemQuotation.kind) }
        options={ kindChoices }
        onChange={ val => onChange({ kind: val.value }) }
        isSearchable={ false } />
    </>,
    tax_rules_group_id: <>
      <label>{ i18nScoped.t('tax_rules_group_id') }</label>
      <Select
        id={ `item_quotation_${index}_tax_rules_group` }
        options={ taxes }
        value={ taxes.find(_ => _.id === itemQuotation.tax_rules_group_id) }
        getOptionValue={ val => val.id.toString() }
        getOptionLabel={ val => val.text }
        onChange={ val => onChange({ tax_rules_group_id: val.id }) }
        isSearchable={ false } />
    </>,
    quantity: <>
      <label htmlFor={ `item_quotation_${index}_quantity` }>{ i18nScoped.t('quantity') }</label>
      <StyledInputDecimal
        id={ `item_quotation_${index}_quantity` }
        value={ itemQuotation.quantity }
        placeholder={ i18nScoped.t('quantity') }
        className="u-txtRight"
        onChange={ val => onChange({ quantity: val as number }) } />
    </>,
    price_cents: <>
      <label htmlFor={ `item_quotation_${index}_price` }>{ unitPriceLabel }</label>
      <StyledInputDecimal
        id={ `item_quotation_${index}_price` }
        value={ itemQuotation.price_cents === null ? '' : itemQuotation.price_cents / 100 }
        placeholder={ unitPriceLabel }
        className="u-txtRight"
        onChange={ val => onChange({ price_cents: val === null ? null : Math.round(+val * 100) }) } />
    </>,
    verbose_description: <QuotationDescriptionInputComponent
      description={ itemQuotation.verbose_description }
      offeredServiceDescriptionPlainText={ offeredServiceDescription }
      placeholder={ i18nScoped.t('verbose_description') }
      onChange={ verbose_description => onChange({ verbose_description }) } />
  }

  layout ||= elements => <Grid container spacing={ 1 }>
    <Grid size={{ xs: 12 }}>{ elements.description }</Grid>
    <Grid size={{ xs: 12, sm: 6 }}>{ elements.kind }</Grid>
    <Grid size={{ xs: 12, sm: 6 }}>{ elements.tax_rules_group_id }</Grid>
    <Grid size={{ xs: 12, sm: 6 }}>{ elements.quantity }</Grid>
    <Grid size={{ xs: 12, sm: 6 }}>{ elements.price_cents }</Grid>
    <Grid size={{ xs: 12 }}>{ elements.verbose_description }</Grid>
  </Grid>

  return layout(parameters)
}